import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Plus, LinkIcon, AlertCircle } from 'lucide-react';
import { processApi, sessionsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { SessionForm } from './SessionForm';

interface SessionInfo {
  id: string;
  session_number: number;
  scheduled_at: string | null;
  status: string;
  duration_minutes: number | null;
  location_type: string;
  content_data: any;
}

interface BookingInfo {
  id: string;
  booking_reference: string;
  start_time: string | null;
  end_time: string | null;
  location_type: string | null;
  notes: string | null;
}

interface UnlinkedSession {
  id: string;
  session_number: number;
  scheduled_at: string | null;
  status: string;
  duration_minutes: number | null;
  location_type: string;
}

interface Props {
  blockId: string;
  caseId: string;
  clientId: string;
  sessionId: string | null;
  sessionNumber: number | null;
  sessionScheduledAt: string | null;
  sessionStatus: string | null;
  clientEmail: string;
  onSave: (data: any) => Promise<void>;
  onChanged: () => void;
}

const statusLabels: Record<string, string> = {
  scheduled: 'Geplant',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
  cancelled: 'Abgesagt',
  no_show: 'Nicht erschienen',
};

const locationLabels: Record<string, string> = {
  in_person: 'Präsenz',
  online: 'Online',
  phone: 'Telefon',
  hybrid: 'Hybrid',
};

const fmtDate = (d: string) => new Date(d).toLocaleDateString('de-DE');
const fmtTime = (d: string) => new Date(d).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

export default function SessionBlockContent({
  blockId, caseId, clientId, sessionId, sessionNumber, sessionScheduledAt, sessionStatus,
  clientEmail, onSave, onChanged,
}: Props) {
  const { showToast } = useToast();

  // If session is linked, show the session form directly
  if (sessionId) {
    return (
      <LinkedSessionView
        blockId={blockId}
        caseId={caseId}
        sessionId={sessionId}
        sessionNumber={sessionNumber}
        sessionScheduledAt={sessionScheduledAt}
        sessionStatus={sessionStatus}
        clientEmail={clientEmail}
        onSave={onSave}
        onChanged={onChanged}
      />
    );
  }

  // No session linked — show setup view
  return (
    <SetupView
      blockId={blockId}
      caseId={caseId}
      clientId={clientId}
      onChanged={onChanged}
    />
  );
}


// === LINKED SESSION VIEW ===
function LinkedSessionView({
  blockId, caseId, sessionId, sessionNumber, sessionScheduledAt, sessionStatus,
  clientEmail, onSave, onChanged,
}: {
  blockId: string;
  caseId: string;
  sessionId: string;
  sessionNumber: number | null;
  sessionScheduledAt: string | null;
  sessionStatus: string | null;
  clientEmail: string;
  onSave: (data: any) => Promise<void>;
  onChanged: () => void;
}) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [allSessions, setAllSessions] = useState<SessionInfo[]>([]);

  useEffect(() => {
    loadSession();
  }, [sessionId]); // eslint-disable-line

  const loadSession = async () => {
    try {
      const [sesRes, listRes] = await Promise.all([
        sessionsApi.get(sessionId),
        sessionsApi.list({ case_id: caseId }),
      ]);
      setSession(sesRes.data);
      setAllSessions(listRes.data.items || listRes.data || []);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Sitzung wird geladen...</div>;
  }

  if (!session) {
    return <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Sitzung nicht gefunden.</div>;
  }

  // Previous session notes for context
  const previousSessionNotes = (() => {
    const prev = allSessions.find((s) => s.session_number === session.session_number - 1);
    if (!prev || !prev.content_data) return undefined;
    const d = prev.content_data;
    const parts: string[] = [];
    if (d.ergebnisse) parts.push(`Ergebnisse: ${d.ergebnisse}`);
    if (d.vereinbarungen) parts.push(`Vereinbarungen: ${d.vereinbarungen}`);
    if (d.hausaufgaben_naechste) parts.push(`Hausaufgaben: ${d.hausaufgaben_naechste}`);
    return parts.length > 0 ? parts.join('\n\n') : undefined;
  })();

  return (
    <SessionForm
      data={session.content_data || {}}
      onSave={async (contentData) => {
        await sessionsApi.update(session.id, { content_data: contentData });
        await loadSession();
      }}
      blockId={blockId}
      clientEmail={clientEmail}
      sessionId={session.id}
      sessionNumber={session.session_number}
      sessionDate={session.scheduled_at || undefined}
      sessionDuration={session.duration_minutes || undefined}
      sessionFormat={session.location_type}
      sessionStatus={session.status}
      onSessionUpdate={async (data) => {
        await sessionsApi.update(session.id, data);
        await loadSession();
        onChanged();
      }}
      previousSessionNotes={previousSessionNotes}
    />
  );
}


// === SETUP VIEW (no session linked yet) ===
function SetupView({
  blockId, caseId, clientId, onChanged,
}: {
  blockId: string;
  caseId: string;
  clientId: string;
  onChanged: () => void;
}) {
  const { showToast } = useToast();
  const [mode, setMode] = useState<'choose' | 'booking' | 'new' | 'unlinked'>('choose');
  const [bookings, setBookings] = useState<BookingInfo[]>([]);
  const [unlinkedSessions, setUnlinkedSessions] = useState<UnlinkedSession[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingUnlinked, setLoadingUnlinked] = useState(false);
  const [linking, setLinking] = useState(false);

  // New session form
  const [newDate, setNewDate] = useState('');

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const res = await processApi.unlinkedBookings(clientId);
      setBookings(res.data || []);
    } catch {
      setBookings([]);
    }
    setLoadingBookings(false);
  };

  const loadUnlinkedSessions = async () => {
    setLoadingUnlinked(true);
    try {
      const res = await processApi.unlinkedSessions(caseId);
      setUnlinkedSessions(res.data || []);
    } catch {
      setUnlinkedSessions([]);
    }
    setLoadingUnlinked(false);
  };

  const handleLinkBooking = async (bookingId: string) => {
    setLinking(true);
    try {
      await processApi.linkSession(blockId, { booking_id: bookingId });
      showToast('Sitzung aus Buchung erstellt und verknüpft.', 'success');
      onChanged();
    } catch {
      showToast('Verknüpfung fehlgeschlagen.', 'error');
    }
    setLinking(false);
  };

  const handleLinkExistingSession = async (sessionId: string) => {
    setLinking(true);
    try {
      await processApi.linkSession(blockId, { session_id: sessionId });
      showToast('Sitzung verknüpft.', 'success');
      onChanged();
    } catch {
      showToast('Verknüpfung fehlgeschlagen.', 'error');
    }
    setLinking(false);
  };

  const handleCreateWithDate = async () => {
    if (!newDate) return;
    setLinking(true);
    try {
      await processApi.linkSession(blockId, { new_session_date: newDate });
      showToast('Neue Sitzung erstellt und verknüpft.', 'success');
      onChanged();
    } catch {
      showToast('Erstellung fehlgeschlagen.', 'error');
    }
    setLinking(false);
  };

  const handleCreateWithoutDate = async () => {
    setLinking(true);
    try {
      await processApi.linkSession(blockId, {});
      showToast('Sitzung ohne Termin erstellt.', 'success');
      onChanged();
    } catch {
      showToast('Erstellung fehlgeschlagen.', 'error');
    }
    setLinking(false);
  };

  // Main choice view
  if (mode === 'choose') {
    return (
      <div style={{ padding: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
          background: 'rgba(var(--warning-rgb, 184,134,11), 0.08)',
          borderRadius: 'var(--radius-sm)', marginBottom: 16,
          fontSize: '0.85rem', color: 'var(--text-light)',
        }}>
          <AlertCircle size={16} style={{ color: 'var(--warning, #B8860B)', flexShrink: 0 }} />
          <span>Dieser Baustein ist noch nicht mit einer Sitzung verknüpft. Wähle eine Option:</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {/* Option 1: From booking */}
          <button
            onClick={() => { setMode('booking'); loadBookings(); }}
            style={{
              border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)',
              padding: 16, textAlign: 'center', cursor: 'pointer', background: 'var(--bg-card)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>📅</div>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Aus Buchung</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Bestehende Buchung des Klienten verwenden
            </div>
          </button>

          {/* Option 2: New date */}
          <button
            onClick={() => setMode('new')}
            style={{
              border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)',
              padding: 16, textAlign: 'center', cursor: 'pointer', background: 'var(--bg-card)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>🕐</div>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Neuer Termin</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Datum und Uhrzeit manuell eingeben
            </div>
          </button>

          {/* Option 3: Without date */}
          <button
            onClick={handleCreateWithoutDate}
            disabled={linking}
            style={{
              border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)',
              padding: 16, textAlign: 'center', cursor: linking ? 'wait' : 'pointer', background: 'var(--bg-card)',
              transition: 'all 0.15s', opacity: linking ? 0.6 : 1,
            }}
            onMouseEnter={(e) => { if (!linking) e.currentTarget.style.borderColor = 'var(--primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>📝</div>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Ohne Termin</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Sitzung anlegen, Termin später ergänzen
            </div>
          </button>
        </div>

        {/* Also show unlinked sessions if any exist */}
        <button
          onClick={() => { setMode('unlinked'); loadUnlinkedSessions(); }}
          className="btn btn-sm btn-secondary"
          style={{ marginTop: 12 }}
        >
          <LinkIcon size={14} /> Bestehende Sitzung verknüpfen
        </button>
      </div>
    );
  }

  // Booking picker
  if (mode === 'booking') {
    return (
      <div style={{ padding: 8 }}>
        <button className="btn btn-sm btn-secondary" onClick={() => setMode('choose')} style={{ marginBottom: 12 }}>
          ← Zurück
        </button>
        <h4 style={{ fontSize: '0.9rem', marginBottom: 12 }}>Buchung auswählen</h4>
        {loadingBookings ? (
          <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)' }}>Buchungen werden geladen...</div>
        ) : bookings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Keine offenen Buchungen für diesen Klienten gefunden.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {bookings.map((b) => (
              <div
                key={b.id}
                className="card"
                style={{ padding: '10px 14px', cursor: linking ? 'wait' : 'pointer', transition: 'box-shadow 0.15s' }}
                onClick={() => !linking && handleLinkBooking(b.id)}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {b.start_time && (
                    <div style={{
                      width: 48, height: 48, borderRadius: 8,
                      background: 'rgba(var(--primary-rgb, 176,116,104), 0.08)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>
                        {new Date(b.start_time).getDate()}
                      </div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        {new Date(b.start_time).toLocaleDateString('de-DE', { month: 'short' })}
                      </div>
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.88rem' }}>
                      {b.start_time ? `${fmtDate(b.start_time)}, ${fmtTime(b.start_time)}` : 'Kein Datum'}
                      {b.end_time && ` – ${fmtTime(b.end_time)}`}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      Ref: {b.booking_reference}
                      {b.location_type && ` · ${locationLabels[b.location_type] || b.location_type}`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // New session with date
  if (mode === 'new') {
    return (
      <div style={{ padding: 8 }}>
        <button className="btn btn-sm btn-secondary" onClick={() => setMode('choose')} style={{ marginBottom: 12 }}>
          ← Zurück
        </button>
        <h4 style={{ fontSize: '0.9rem', marginBottom: 12 }}>Neuen Termin erstellen</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 500, marginBottom: 4, display: 'block' }}>
              Datum und Uhrzeit
            </label>
            <input
              type="datetime-local"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="form-control"
              style={{
                width: '100%', padding: '8px 12px',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                fontSize: '0.85rem', background: 'var(--bg-card)',
              }}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCreateWithDate}
            disabled={!newDate || linking}
          >
            <Plus size={14} /> {linking ? 'Wird erstellt...' : 'Sitzung erstellen'}
          </button>
        </div>
      </div>
    );
  }

  // Unlinked sessions picker
  if (mode === 'unlinked') {
    return (
      <div style={{ padding: 8 }}>
        <button className="btn btn-sm btn-secondary" onClick={() => setMode('choose')} style={{ marginBottom: 12 }}>
          ← Zurück
        </button>
        <h4 style={{ fontSize: '0.9rem', marginBottom: 12 }}>Bestehende Sitzung verknüpfen</h4>
        {loadingUnlinked ? (
          <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)' }}>Wird geladen...</div>
        ) : unlinkedSessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Keine unverknüpften Sitzungen in diesem Fall gefunden.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {unlinkedSessions.map((s) => (
              <div
                key={s.id}
                className="card"
                style={{ padding: '10px 14px', cursor: linking ? 'wait' : 'pointer', transition: 'box-shadow 0.15s' }}
                onClick={() => !linking && handleLinkExistingSession(s.id)}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'var(--primary)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 600, fontSize: '0.85rem', flexShrink: 0,
                  }}>
                    {s.session_number}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.88rem' }}>
                      Sitzung {s.session_number}
                    </div>
                    <div className="text-muted text-sm" style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                      {s.scheduled_at && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={12} /> {fmtDate(s.scheduled_at)}, {fmtTime(s.scheduled_at)}
                        </span>
                      )}
                      {s.duration_minutes && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={12} /> {s.duration_minutes} Min.
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`badge ${s.status === 'completed' ? 'badge-active' : 'badge-new'}`}>
                    {statusLabels[s.status] || s.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}
